/**
 * EndorsementsList — the learner's received endorsements, with a per-item
 * "Show on public portfolio" toggle the learner alone controls (default off).
 *
 * Presentational: data and the toggle handler come from the container
 * (MyEndorsementsSection), so the rendering is testable without a store.
 * Revoked endorsements stay listed (history) but cannot be published.
 */

import {
  EVIDENCE_KIND_LABELS,
  type Endorsement,
} from './endorsementTypes';

export interface EndorsementsListProps {
  endorsements: Endorsement[];
  /** Whether the learner's portfolio is public; published items appear only then. */
  portfolioIsPublic: boolean;
  onToggleVisibility: (id: string, visibleOnPortfolio: boolean) => void;
  /** Id of the endorsement whose toggle is being saved. */
  pendingId?: string | null;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export function EndorsementsList({
  endorsements,
  portfolioIsPublic,
  onToggleVisibility,
  pendingId,
}: EndorsementsListProps) {
  if (endorsements.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="endorsements-empty">
        No endorsements yet. When a manager in your workspace acknowledges a piece of your work, it appears here.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {!portfolioIsPublic && (
        <p className="text-xs text-gray-500" data-testid="endorsements-private-note">
          Your profile is private, so published endorsements are not shown to anyone until you make it public.
        </p>
      )}
      <ul className="divide-y border rounded-lg" data-testid="endorsements-list">
        {endorsements.map((e) => {
          const revoked = e.revokedAt !== null;
          const toggleId = `endorsement-visible-${e.id}`;
          return (
            <li key={e.id} className="p-4 flex items-start justify-between gap-4" data-endorsement-id={e.id}>
              <div className="min-w-0">
                <p className="font-medium text-gray-900">
                  {e.endorserDisplayName ?? 'A manager'}
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {EVIDENCE_KIND_LABELS[e.evidenceKind]} &middot; {formatDate(e.createdAt)}
                  </span>
                </p>
                {e.statement && <p className="text-sm text-gray-700 mt-1 break-words">{e.statement}</p>}
                {revoked && (
                  <p className="text-xs text-red-600 mt-1" data-testid="endorsement-revoked">
                    Revoked {e.revokedAt ? formatDate(e.revokedAt) : ''}
                    {e.revokeReason ? `: ${e.revokeReason}` : ''}
                  </p>
                )}
              </div>
              <label htmlFor={toggleId} className="flex items-center gap-2 text-sm text-gray-700 shrink-0">
                <input
                  id={toggleId}
                  type="checkbox"
                  className="toggle"
                  checked={e.visibleOnPortfolio && !revoked}
                  disabled={revoked || pendingId === e.id}
                  onChange={(ev) => onToggleVisibility(e.id, ev.target.checked)}
                  aria-label="Show on public portfolio"
                />
                Show on public portfolio
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default EndorsementsList;
